import { useMemo } from "react";
import type { CampaignMetrics } from "@/hooks/api/use-campaign-metrics";
import { useSelectableMetrics } from "@/hooks/api/use-metrics-definitions";
import { useCampaignColumnSelection } from "@/hooks/use-column-selection";
import { getCampaignColumns } from "@/pages/campaigns/campaign-list/campaign-columns";
import {
  EntityColumnSelector,
  type SelectableMetric,
} from "./entity-column-selector";

interface CampaignsColumnSelectorProps {
  selectedMetrics?: Array<keyof CampaignMetrics>;
  onSelectMetrics?: (metrics: Array<keyof CampaignMetrics>) => void;
  showMetricsSection?: boolean;
  defaultMetrics?: Array<keyof CampaignMetrics>;
}

/**
 * Component for managing campaign column selection including both core and custom fields,
 * and optionally metrics. Displays a popover with checkboxes for selecting which columns to display.
 */
export function CampaignsColumnSelector({
  selectedMetrics = [],
  onSelectMetrics,
  showMetricsSection = false,
  defaultMetrics = [],
}: CampaignsColumnSelectorProps) {
  // Get the core columns
  const coreColumns = getCampaignColumns();

  // Use the new hook that works with both core and custom columns
  const { availableColumns, isColumnSelected, toggleColumn, isLoading, error } =
    useCampaignColumnSelection(coreColumns, selectedMetrics);

  // Filter out metric columns from availableColumns since they're handled separately
  const filteredAvailableColumns = useMemo(() => {
    return availableColumns.filter((column) => column.columnType !== "metric");
  }, [availableColumns]);

  // Fetch metrics using the new hook
  const { data: fetchedMetrics } = useSelectableMetrics("campaigns");

  // Use fetched metrics if available, otherwise empty array
  const availableMetrics = useMemo<SelectableMetric[]>(() => {
    if (!showMetricsSection) return [];
    return fetchedMetrics || [];
  }, [showMetricsSection, fetchedMetrics]);

  // Convert selected metrics to string array for the EntityColumnSelector
  const selectedMetricIds = useMemo(() => {
    return selectedMetrics.map((metric) => metric as string);
  }, [selectedMetrics]);

  // Handle metrics selection
  const handleSelectMetrics = (metricIds: string[]) => {
    if (onSelectMetrics) {
      onSelectMetrics(metricIds as Array<keyof CampaignMetrics>);
    }
  };

  return (
    <EntityColumnSelector
      availableColumns={filteredAvailableColumns}
      isColumnSelected={isColumnSelected}
      toggleColumn={toggleColumn}
      isLoading={isLoading}
      error={error}
      buttonLabel="Columns"
      noColumnsMessage="No columns available."
      errorMessage="Error loading columns."
      // Metrics props
      availableMetrics={availableMetrics}
      selectedMetrics={selectedMetricIds}
      onSelectMetrics={handleSelectMetrics}
      showMetricsSection={showMetricsSection}
      defaultMetrics={defaultMetrics}
    />
  );
}
